/**
 * Datenzugriff der Karte „Versände der letzten Woche" (Workshop-Lücke N4, Plan 2026-10-29) im Kapitel Kanäle.
 * Backend: `GET /api/auswertungen/kanaele/rueckblick?woche=YYYY-Www` → `services/auswertungen/rueckblickService.ts`.
 * Typen spiegeln die Antwort dort.
 */
import { fetchData } from './apiUtils';

export interface RueckblickWoche {
  woche: string;
  /** Montag, inklusive. */
  von: string;
  /** Montag danach, exklusiv. */
  bis: string;
}

export interface RueckblickZahlen {
  versaende: number;
  kontakte: number;
  anfos: number;
  anfos_vorlaeufig: number;
  anfos_je_1000: number | null;
  /** Erwarteter Wert bisher (vorläufig). */
  wert: number;
  wert_je_1000: number | null;
}

export interface RueckblickVergleich {
  anfos_je_1000: number | null;
  wert_je_1000: number | null;
  versaende: number;
  kontakte: number;
  anfos: number | null;
}

export interface RueckblickVersand extends RueckblickZahlen {
  schluessel: string;
  send_date: string;
  alter_tage: number;
  name: string | null;
  client_code: string | null;
  ohne_kontakte: boolean;
  vergleich: RueckblickVergleich;
}

export type RueckblickFlaecheKey = 'plx_dm_ft' | 'plx_dm_schwester' | 'plx_dm_partner' | 'nl_redlink_plx';

export interface RueckblickFlaeche extends RueckblickZahlen {
  flaeche: RueckblickFlaecheKey;
  label: string;
  art: 'direkt_mailing' | 'redlink';
  versand: RueckblickVersand[];
  vergleich: RueckblickVergleich;
}

export interface RueckblickZelle {
  liste: string;
  label: string;
  welle: 'vortag' | 'frueh' | 'spaet';
  welle_label: string;
  kontakte: number;
  anmeldungen: number;
  anmeldungen_je_1000: number | null;
  vergleich: { anmeldungen_je_1000: number | null; kontakte: number; anmeldungen: number; webinare: number } | null;
}

export interface RueckblickWebinar {
  webinar_id: string;
  public_title: string | null;
  start_date: string;
  alter_tage: number;
  fehlende_wellen: number;
  zellen: RueckblickZelle[];
  anfos: {
    anfos: number;
    anfos_vorlaeufig: number;
    /** null = keine Anfo bewertet (Anzeige „—“), nie 0 € für „unbekannt“. */
    wert: number | null;
    mit_wert: number;
    vergleich: { anfos_mittel: number | null; wert_mittel: number | null; webinare: number };
  };
}

export interface RueckblickAntwort {
  as_of: string;
  /**
   * Bis hierhin zählen eigene Zahl und Vergleich (PL-X-Datenstand `YYYY-MM-DD HH:mm:ss`, höchstens Ende des Stichtags);
   * die Karte zeigt ihn als „Stand" (Audit 30.10.2026 B5). Optional bis zum Deploy.
   */
  datenstand?: string;
  woche: RueckblickWoche;
  letzte_volle_woche: string;
  wochen_auswahl: RueckblickWoche[];
  vorwochen: number;
  grau_schwellen: { anfos: number; anmeldungen: number; rechtzeitig: number; abmeldungen: number; nach_beginn_anmeldungen: number };
  redlink_daten_ab: string;
  bestell_link: { basis_von: string; basis_bis: string; flaechen: RueckblickFlaeche[] };
  anmeldung: {
    basis_soll: number;
    basis: { webinar_id: string; public_title: string | null; start_date: string }[];
    webinare: RueckblickWebinar[];
  };
}

export async function fetchRueckblick(woche?: string): Promise<RueckblickAntwort> {
  const url = `/api/auswertungen/kanaele/rueckblick${woche ? `?woche=${encodeURIComponent(woche)}` : ''}`;
  const res = await fetchData<RueckblickAntwort>(url);
  if (!res.success || res.data === undefined) throw new Error(res.error || 'Laden fehlgeschlagen');
  return res.data;
}
